/**
 * Pure decision for what an Enter keydown should do inside the Autocomplete
 * combobox. Kept separate from the JSX so it is unit-testable in the project's
 * node-env Vitest setup (no jsdom / rendered DOM).
 *
 * Contract (F2 — Enter must never leak to a form's implicit submit while the
 * user is interacting with the combobox):
 *  - dropdown open + an active option           → 'select'     (pick activeIndex)
 *  - dropdown open + exactly one option, none    → 'selectOnly' (pick the obvious match, index 0)
 *    highlighted
 *  - dropdown open, otherwise                    → 'preventOnly' (swallow Enter, keep menu open)
 *  - dropdown closed                             → 'ignore'      (let Enter behave normally)
 *
 * F3/AUTOORBIT-505 opt-in — when `autoHighlightFirst` is `false` the caller does
 * NOT pre-highlight the first filtered row, so Enter must not silently commit a
 * partial substring match. In that mode Enter commits ONLY when a row is
 * explicitly highlighted (activeIndex >= 0) or the typed `query` is an exact,
 * unambiguous (single) case-insensitive match of an option label; otherwise it
 * swallows Enter and keeps the menu open ('preventOnly'). The default
 * (`autoHighlightFirst = true`) preserves the historical behaviour byte-for-byte
 * and never reads `query`/`getOptionLabel`.
 *
 * Every non-'ignore' action means the caller MUST call preventDefault (i.e. Enter
 * never leaks to a form submit while the dropdown is open).
 *
 * @param {{
 *   open: boolean,
 *   activeIndex: number,
 *   options: unknown[],
 *   autoHighlightFirst?: boolean,
 *   query?: string,
 *   getOptionLabel?: (option: unknown) => string,
 * }} state
 * @returns {{ action: 'select'|'selectOnly'|'preventOnly'|'ignore', index: number }}
 */
export function resolveEnter({
  open,
  activeIndex,
  options = [],
  autoHighlightFirst = true,
  query = '',
  getOptionLabel = (o) => o?.label ?? '',
}) {
  if (!open) {
    return { action: 'ignore', index: -1 };
  }
  if (activeIndex >= 0 && activeIndex < options.length) {
    return { action: 'select', index: activeIndex };
  }
  if (autoHighlightFirst) {
    if (options.length === 1) {
      return { action: 'selectOnly', index: 0 };
    }
    return { action: 'preventOnly', index: -1 };
  }
  // No silent first-match commit: only an exact, single case-insensitive label
  // match may commit on Enter; anything else keeps the menu open.
  const needle = String(query ?? '').trim().toLowerCase();
  if (needle !== '') {
    const exactMatches = [];
    options.forEach((option, index) => {
      if (String(getOptionLabel(option) ?? '').trim().toLowerCase() === needle) {
        exactMatches.push(index);
      }
    });
    if (exactMatches.length === 1) {
      return { action: 'selectOnly', index: exactMatches[0] };
    }
  }
  return { action: 'preventOnly', index: -1 };
}

export default resolveEnter;
